import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import TurboClaimClient from '@/api/client';
import { env } from '@/config/env';
import type { WidgetPublicConfig, WidgetSubmitResponse } from '@/api/generated/client';
import type { InitOverrideFunction } from '@/api/generated/client/runtime';

const publicApiClient = new TurboClaimClient({
  basePath: env.apiBaseUrl,
});

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      }) => string;
      getResponse: (widgetId: string) => string;
      reset: (widgetId: string) => void;
    };
  }
}

export type WidgetFormState = {
  homeownerFirstName: string;
  homeownerLastName: string;
  homeownerEmail: string;
  homeownerPhone: string;
  homeownerPhone2: string;
  homeownerEmail2: string;
  propertyStreetAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  mailingAddress: string;
  dateOfLoss: string;
  causeOfLoss: string;
  insuranceCompany: string;
  contractor: string;
  contractorContact: string;
  contractorEmail: string;
  contractorPhone: string;
  policyNumber: string;
  description: string;
  companyWebsite: string;
};

export type WidgetLinkDraft = {
  url: string;
};

export type EmbedStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

export const MAX_DOCUMENTS = 3;
export const MAX_FILE_BYTES = 4_400_000;
export const MAX_FILE_MB_DISPLAY = (MAX_FILE_BYTES / 1_000_000).toFixed(1).replace(/\.0$/, '');
export const MAX_LINKS = 5;
export const MAX_PROPERTY_ADDRESS_LENGTH = 500;

const EMPTY_FORM: WidgetFormState = {
  homeownerFirstName: '',
  homeownerLastName: '',
  homeownerEmail: '',
  homeownerPhone: '',
  homeownerPhone2: '',
  homeownerEmail2: '',
  propertyStreetAddress: '',
  propertyCity: '',
  propertyState: '',
  propertyZipCode: '',
  mailingAddress: '',
  dateOfLoss: '',
  causeOfLoss: '',
  insuranceCompany: '',
  contractor: '',
  contractorContact: '',
  contractorEmail: '',
  contractorPhone: '',
  policyNumber: '',
  description: '',
  companyWebsite: '',
};

function readSearchParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

function detectEmbedderOrigin(): string | undefined {
  const referrer = document.referrer;
  if (!referrer) {
    return undefined;
  }

  try {
    return new URL(referrer).origin;
  } catch {
    return undefined;
  }
}

function mapFilesToDocuments(files: File[]): Promise<Array<{ filename: string; mimeType: string; fileContentBase64: string }>> {
  if (!files || files.length === 0) {
    return Promise.resolve([]);
  }

  return Promise.all(
    Array.from(files).map(async (file) => {
      const fileContentBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = typeof reader.result === 'string' ? reader.result : '';
          const base64Payload = result.includes(',') ? result.split(',')[1] : result;
          resolve(base64Payload);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      return {
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        fileContentBase64,
      };
    }),
  );
}

function deriveLinkLabel(url: string): string {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    const sanitized = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
    const firstPart = sanitized.split('.')[0];

    if (firstPart && firstPart.length > 0) {
      return firstPart;
    }
  } catch {
    // Fallback to URL when parsing fails.
  }

  return url;
}

export interface UseWidgetEmbedResult {
  publicKey: string;
  config: WidgetPublicConfig | null;
  formState: WidgetFormState;
  links: WidgetLinkDraft[];
  documents: File[];
  status: EmbedStatus;
  message: string;
  submitResult: WidgetSubmitResponse | null;
  isCaptchaRequired: boolean;
  isCaptchaMissing: boolean;
  onFieldChange: (field: keyof WidgetFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  addLink: () => void;
  updateLink: (index: number, patch: Partial<WidgetLinkDraft>) => void;
  removeLink: (index: number) => void;
  removeDocument: (index: number) => void;
  handleFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
}

export function useWidgetEmbed(): UseWidgetEmbedResult {
  const [publicKey, setPublicKey] = useState<string>('');
  const [config, setConfig] = useState<WidgetPublicConfig | null>(null);
  const [formState, setFormState] = useState<WidgetFormState>(EMPTY_FORM);
  const [documents, setDocuments] = useState<File[]>([]);
  const [captchaWidgetId, setCaptchaWidgetId] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [links, setLinks] = useState<WidgetLinkDraft[]>([]);
  const [status, setStatus] = useState<EmbedStatus>('loading');
  const [message, setMessage] = useState<string>('');
  const [submitResult, setSubmitResult] = useState<WidgetSubmitResponse | null>(null);

  const embedderOrigin = useMemo(() => detectEmbedderOrigin(), []);
  const withWidgetOriginHeader = useMemo<InitOverrideFunction | undefined>(() => {
    if (!embedderOrigin) {
      return undefined;
    }

    return async ({ init }) => ({
      ...init,
      headers: {
        ...(init.headers || {}),
        'X-Widget-Origin': embedderOrigin,
      },
    });
  }, [embedderOrigin]);
  const isCaptchaRequired = Boolean(config?.captcha);
  const isCaptchaMissing = isCaptchaRequired && captchaToken.length === 0;

  useEffect(() => {
    const key = readSearchParams().get('key') || '';
    setPublicKey(key);

    if (!key) {
      setStatus('error');
      setMessage('Missing widget key.');
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const widgetConfig = await publicApiClient.claimWidgets.getPublicClaimWidgetConfig(
          { publicKey: key },
          withWidgetOriginHeader,
        );
        if (cancelled) {
          return;
        }
        setConfig(widgetConfig);
        setStatus('ready');
      } catch (error) {
        if (!cancelled) {
          setStatus('error');
          setMessage(error instanceof Error ? error.message : 'Unable to load widget.');
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [withWidgetOriginHeader]);

  useEffect(() => {
    if (!config?.captcha || !config.captcha.siteKey) {
      return;
    }

    const siteKey = config.captcha.siteKey;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.turnstile || captchaWidgetId) {
        return;
      }

      const widgetId = window.turnstile.render('#turnstile-container', {
        sitekey: siteKey,
        callback: (token) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(''),
        'error-callback': () => setCaptchaToken(''),
      });
      setCaptchaWidgetId(widgetId);
    };

    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [config, captchaWidgetId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Resize postMessage is only needed when rendered in an iframe embed.
    if (window.parent === window) {
      return;
    }

    const targetOrigin = embedderOrigin || '*';

    const postSize = () => {
      const height = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      window.parent.postMessage({ type: 'turboclaim-widget:resize', height }, targetOrigin);
    };

    postSize();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(postSize);
    observer.observe(document.body);

    return () => observer.disconnect();
  }, [embedderOrigin]);

  const onFieldChange = (field: keyof WidgetFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const addLink = () => {
    setLinks((prev) => {
      if (prev.length >= MAX_LINKS) {
        setMessage(`You can add at most ${MAX_LINKS} reference links.`);
        return prev;
      }

      setMessage('');
      return [...prev, { url: '' }];
    });
  };

  const updateLink = (index: number, patch: Partial<WidgetLinkDraft>) => {
    setLinks((prev) => prev.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
  };

  const removeLink = (index: number) => {
    setLinks((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files;

    if (!selected || selected.length === 0) {
      return;
    }

    const selectedFiles = Array.from(selected);

    const oversized = selectedFiles.find((file) => file.size > MAX_FILE_BYTES);
    if (oversized) {
      setMessage(`"${oversized.name}" is too large. Each file must be under ${MAX_FILE_MB_DISPLAY}MB.`);
      event.target.value = '';
      return;
    }

    if (documents.length + selectedFiles.length > MAX_DOCUMENTS) {
      setMessage(`You can attach at most ${MAX_DOCUMENTS} documents.`);
      event.target.value = '';
      return;
    }

    setMessage('');
    setDocuments((prev) => [...prev, ...selectedFiles]);
    event.target.value = '';
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!publicKey || status === 'submitting') {
      return;
    }

    if (isCaptchaMissing) {
      setMessage('Please complete the captcha before submitting.');
      return;
    }

    const propertyAddress = `${formState.propertyStreetAddress.trim()}, ${formState.propertyCity.trim()}, ${formState.propertyState.trim().toUpperCase()} ${formState.propertyZipCode.trim()}`;
    if (propertyAddress.length > MAX_PROPERTY_ADDRESS_LENGTH) {
      setMessage(`Property address is too long. Please keep it under ${MAX_PROPERTY_ADDRESS_LENGTH} characters.`);
      return;
    }

    if (links.length > MAX_LINKS) {
      setMessage(`You can add at most ${MAX_LINKS} reference links.`);
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const mappedDocuments = await mapFilesToDocuments(documents);
      const mappedLinks = links
        .map((link) => {
          const url = link.url.trim();
          return {
            label: deriveLinkLabel(url),
            url,
          };
        })
        .filter((link) => link.url.length > 0);

      const idempotencyKey = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `widget-${Date.now()}`;

      const homeownerName = `${formState.homeownerFirstName} ${formState.homeownerLastName}`.trim().replace(/\s+/g, ' ');

      const payload = {
        homeownerName,
        homeownerEmail: formState.homeownerEmail,
        homeownerPhone: formState.homeownerPhone,
        homeownerPhone2: formState.homeownerPhone2 || undefined,
        homeownerEmail2: formState.homeownerEmail2 || undefined,
        propertyAddress,
        mailingAddress: formState.mailingAddress || undefined,
        dateOfLoss: formState.dateOfLoss || undefined,
        causeOfLoss: formState.causeOfLoss || undefined,
        insuranceCompany: formState.insuranceCompany || undefined,
        contractor: formState.contractor || undefined,
        contractorContact: formState.contractorContact || undefined,
        contractorEmail: formState.contractorEmail || undefined,
        contractorPhone: formState.contractorPhone || undefined,
        policyNumber: formState.policyNumber || undefined,
        description: formState.description || undefined,
        links: mappedLinks,
        documents: mappedDocuments,
        parentOrigin: embedderOrigin,
        captchaToken: captchaToken || undefined,
        companyWebsite: formState.companyWebsite,
        idempotencyKey,
      };

      const result = await publicApiClient.claimWidgets.submitPublicClaimWidget({
        publicKey,
        widgetSubmitRequest: payload,
      }, withWidgetOriginHeader);

      setSubmitResult(result);
      setStatus('success');

      if (captchaWidgetId && window.turnstile) {
        window.turnstile.reset(captchaWidgetId);
      }
    } catch (error) {
      setStatus('ready');
      setMessage(error instanceof Error ? error.message : 'Unable to submit. Please try again.');
    }
  };

  return {
    publicKey,
    config,
    formState,
    links,
    documents,
    status,
    message,
    submitResult,
    isCaptchaRequired,
    isCaptchaMissing,
    onFieldChange,
    addLink,
    updateLink,
    removeLink,
    removeDocument,
    handleFileChange,
    handleSubmit,
  };
}
